import React, { useEffect, useState } from "react";
import { Turnstile } from "@marsidev/react-turnstile";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Shield, AlertCircle } from "lucide-react";

class TurnstileErrorBoundary extends React.Component<
  { children: React.ReactNode; onCrash?: () => void },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    console.error('Turnstile widget crashed:', error);
    this.props.onCrash?.();
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="text-center space-y-2">
          <AlertCircle className="w-10 h-10 text-destructive mx-auto" />
          <p className="text-sm font-medium">Verification is not available in this browser.</p>
          <p className="text-xs text-muted-foreground">
            Try disabling Brave Shields for this site, or use another browser.
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}

interface TurnstileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (token: string) => void;
  siteKey?: string;
  isDevelopment?: boolean;
}

export const TurnstileModal = ({
  isOpen,
  onClose,
  onSuccess,
  siteKey,
  isDevelopment = false,
}: TurnstileModalProps) => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [widgetCrashed, setWidgetCrashed] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setIsVerifying(false);
      setError(null);
      setWidgetCrashed(false);
    }
  }, [isOpen]);

  const handleSuccess = (token: string) => {
    setIsVerifying(false);
    setError(null);
    onSuccess(token);
    onClose();
  };

  const handleError = () => {
    setIsVerifying(false);
    setError("Verification failed. Please try again.");
  };

  const handleExpire = () => {
    setIsVerifying(false);
    setError("Verification expired. Please try again.");
  };

  const handleWidgetLoad = () => {
    setIsVerifying(true);
    setError(null);
  };

  // In development, auto-verify without showing Turnstile
  useEffect(() => {
    if (isOpen && isDevelopment) {
      // Simulate immediate verification in development
      const timer = setTimeout(() => {
        onSuccess("dev-token");
        onClose();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isDevelopment, onSuccess, onClose]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            <DialogTitle>Security Verification</DialogTitle>
          </div>
          <DialogDescription>
            Please complete the security check below to continue using the app.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center space-y-4 py-4">
          {!isDevelopment && siteKey ? (
            <>
              {error && (
                <div className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 rounded-md w-full">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}
              
              <div className="w-full flex justify-center">
                <TurnstileErrorBoundary onCrash={() => setWidgetCrashed(true)}>
                  <Turnstile
                    siteKey={siteKey}
                    onSuccess={handleSuccess}
                    onError={handleError}
                    onExpire={handleExpire}
                    onLoad={handleWidgetLoad}
                  />
                </TurnstileErrorBoundary>
              </div>

              {isVerifying && !widgetCrashed && (
                <p className="text-sm text-muted-foreground">
                  Verifying...
                </p>
              )}
            </>
          ) : (
            <div className="text-center space-y-2">
              <Shield className="w-12 h-12 text-primary mx-auto" />
              <p className="text-sm text-muted-foreground">
                Development mode - verification bypassed
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
